import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';

import type {
  PatientProfileSearchResult,
  PatientSearchedMeasurements,
} from '../../contracts';
import { SEARCH_PATIENT_PROFILES } from '../../contracts';
import { searchPatientProfiles } from '../api/patients';

// Mirrors the BE's `q` min_length=2 (SearchPatientProfilesRequest). The
// previous value of 1 fired a request the BE always rejected with a 422 —
// the Atlas index can't match single characters anyway (edgeGram
// minGrams 2). Sub-2-char queries are covered by the local-first layer
// (useLocalPatientSearch) when its flag is on.
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 150;
const STALE_TIME_MS = 30_000;
const DEFAULT_LIMIT = 10;

/**
 * What this hook can honestly measure about one BE-confirmed search.
 *
 * Deliberately only the four facts the QUERY knows. The funnel event also
 * carries `session_id`, `search_id` and `feature_flag_variant`, and those are
 * app context — the delegate merges them in at the dispatch (see below), so
 * the field names here are the analytics field names and the delegate's
 * payload is a plain spread with no renaming step to drift.
 *
 * RE-EXPORT, NOT A DECLARATION. The interface moved to
 * `../../contracts/patient-search-analytics.types` at openspec task 2.1b, next
 * to the `patient_searched` payload it is a subset of — a payload contract that
 * crosses the door belongs in the inert zone. This line keeps
 * `@heidi/patients/data` naming it, which is where the app-side delegate and
 * this hook's own suite import it from.
 */
export type { PatientSearchedMeasurements };

export interface UseSearchPatientProfilesOptions {
  query: string;
  limit?: number;
  enabled?: boolean;
  /**
   * Scopes the BE search to the patient-profile owner resolved from this
   * session. Required for the assistant-acting-for-clinician case —
   * without it, the BE treats the caller as searching their own list and
   * assistants get zero results regardless of what they type (DEV-12028).
   * Undefined falls back to owner-mode (the caller's own profiles), which
   * is correct when there is no session context.
   */
  sessionId?: string;
  /**
   * Fired once per BE response (queryFn invocation — cache hits skip), at the
   * exact position the `patient_searched` dispatch used to fire.
   *
   * It is a PORT, not an analytics call: `@heidi/patients/data` is headless
   * and `no-new-rematch-coupling` blocks `dispatch` in-package, so the event
   * stays an app reaction at the old-path delegate (ARCFE-921 shape). Optional
   * — a consumer that does not participate in the funnel simply omits it, and
   * nothing is emitted.
   */
  onSearched?: (measurements: PatientSearchedMeasurements) => void;
}

export interface UseSearchPatientProfilesResult {
  results: PatientProfileSearchResult[];
  isFetching: boolean;
  /** True when the current query is enabled and its last fetch failed. */
  isError: boolean;
  debouncedQuery: string;
}

/**
 * Debounced Atlas-backed search over `POST /patient-profiles:search`
 * (ARCFE-1057).
 *
 * Carved verbatim out of `src/features/patient-find-or-link/hooks/` — the one
 * file ARCFE-1054's repo-wide sweep found that does real patients data work
 * from a feature folder, where no `legacy.hubs` glob could see it. Two things
 * changed on the way in and nothing else did:
 *
 * 1. the cache identity now comes from `/contracts` instead of being declared
 *    here (`key-single-site`); the old path re-exports it;
 * 2. the Rematch `dispatch.analyticsScribe.trackPatientSearched` became the
 *    `onSearched` port above. The delegate fires the dispatch with the same
 *    payload in the same field order.
 *
 * The debounce, the 2-char gate, `keepPreviousData`, the 30 s stale window,
 * the `signal` pass-through and the clear-on-empty effect are the shipped
 * behaviour and are untouched. `isError` is the one added flag: true once
 * the fetch for the current debounced query has failed after retries.
 */
export function useSearchPatientProfiles({
  query,
  limit = DEFAULT_LIMIT,
  enabled = true,
  sessionId,
  onSearched,
}: UseSearchPatientProfilesOptions): UseSearchPatientProfilesResult {
  const trimmed = query.trim();
  const [debouncedQuery] = useDebounce(trimmed, DEBOUNCE_MS);
  const queryEnabled = enabled && debouncedQuery.length >= MIN_QUERY_LENGTH;

  // Capture the latest port in a ref so the queryFn closure can read it
  // without retriggering re-fetches when only the callback identity changes.
  // The queryKey deliberately excludes it — the legacy body kept the tracking
  // context out of the key for the same reason: a new search_id (per focus)
  // must not invalidate the cached result for a query string the user is
  // still typing.
  const onSearchedRef = useRef(onSearched);
  onSearchedRef.current = onSearched;

  // We deliberately read `onSearchedRef.current` inside queryFn but do NOT
  // include it in queryKey — see the ref comment above. The exhaustive-deps
  // rule can't know that.
  /* eslint-disable @tanstack/query/exhaustive-deps */
  const { data, isFetching, isError } = useQuery({
    queryKey: [SEARCH_PATIENT_PROFILES, debouncedQuery, limit, sessionId],
    enabled: queryEnabled,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const startMs = performance.now();
      const { data } = await searchPatientProfiles(
        { q: debouncedQuery, limit },
        sessionId,
        signal
      );
      const results = data.data;

      // Fire on each BE response. queryFn only runs on network fetches (cache
      // hits skip), so this is naturally per-keystroke-that-hit-the-BE, which
      // is the unit the funnel needs.
      onSearchedRef.current?.({
        query_length: debouncedQuery.length,
        result_count: results.length,
        has_zero_results: results.length === 0,
        latency_ms: Math.round(performance.now() - startMs),
      });

      return results;
    },
  });
  /* eslint-enable @tanstack/query/exhaustive-deps */

  // Clear stale results when the user empties the input rather than
  // showing the previous result set.
  const [results, setResults] = useState<PatientProfileSearchResult[]>([]);
  useEffect(() => {
    if (!queryEnabled) {
      setResults([]);
      return;
    }
    if (data) setResults(data);
  }, [data, queryEnabled]);

  return {
    results,
    isFetching: queryEnabled && isFetching,
    isError: queryEnabled && isError,
    debouncedQuery,
  };
}
